import { jsxs, jsx } from "react/jsx-runtime";
import { useRef, useState, useEffect, useLayoutEffect } from "react";
import styled, { css } from "styled-components";
import { colors, typographyCss, cbSemanticCssVars } from "@creditornot/cb-ingredients";
import { configureCssVars } from "@creditornot/cb-utils";
const { cssString, cssVars } = configureCssVars({
  activeText: {
    light: colors.wolt100,
    lightHighContrast: colors.darkSky100,
    dark: colors.wolt100
  },
  activeTextDisabled: {
    light: colors.wolt64,
    lightHighContrast: colors.darkSky64,
    dark: colors.wolt64
  },
  activeIndicator: {
    light: colors.wolt100,
    lightHighContrast: colors.darkSky100,
    dark: colors.wolt100
  }
});
const TabItemContainer = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
`;
const LeftContent = styled.div`
  margin-inline-end: 4px;
`;
const RightContent = styled.div`
  margin-inline-start: 4px;
`;
const LabelContainer = styled.div`
  position: relative;
  flex: 1;
  flex-shrink: 0;
`;
const Label = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  transition: opacity 100ms ease-in;
  opacity: ${({ $show }) => $show ? 1 : 0};
`;
const LabelOverlay = styled.span`
  overflow: visible;
  font-weight: 700;
  transition: opacity 100ms ease-in;
  opacity: ${({ $show }) => $show ? 1 : 0};
`;
const TabItem = styled.button`
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background: transparent;
  box-sizing: border-box;
  user-select: none;
  height: 100%;
  padding: 8px 24px;

  @media (max-width: 550px) {
    padding-inline-start: 16px;
    padding-inline-end: 16px;
  }

  border: none;
  ${typographyCss.Body3()};
  text-decoration: none;
  color: ${cbSemanticCssVars.textPrimary};
  font-weight: 400;
  cursor: ${({ disabled }) => disabled ? "not-allowed" : "pointer"};
  opacity: ${({ disabled }) => disabled ? 0.6 : 1};

  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;

  ${LabelContainer} {
    &,
    &:visited,
    &:link {
      color: ${({ $active }) => $active ? cssVars.activeText : cbSemanticCssVars.textSecondary};
      text-decoration: none;
    }
  }

  outline: none;
  transition: color 75ms ease-in;

  ${({ $active, disabled }) => !disabled && css`
      &:hover ${LabelContainer} {
        color: ${$active ? cssVars.activeText : cbSemanticCssVars.textPrimary};
      }

      &:active ${LabelContainer} {
        color: ${$active ? cssVars.activeTextDisabled : cbSemanticCssVars.textSecondary};
      }
    `}

  &:not(:active):focus::after {
    ${({ $muteFocusIndicator }) => !$muteFocusIndicator && css`
        content: '';
      `};
    position: absolute;
    left: 0;
    right: 0;
    backface-visibility: hidden;
    bottom: ${({ $activityIndicatorOutside }) => $activityIndicatorOutside ? "-2px" : 0};
    height: 2px;
    transition: background-color 1s cubic-bezier(0.35, 0, 0.25, 1);
    background-color: ${({ $active, disabled }) => !$active && !disabled ? cbSemanticCssVars.a11yFocusIndicator : "transparent"};
  }
`;
const TabItemContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
`;
const Root = styled.div`
  ${cssString}
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  box-sizing: border-box;
  user-select: none;
  position: relative;
  z-index: 0;
  display: ${({ $fixedWidths }) => $fixedWidths ? "flex" : "inline-flex"};
  height: 48px;
  overflow-x: auto;
  width: 100%;

  -ms-overflow-style: none; /* IE, Edge */
  scrollbar-width: none; /* Mozilla */
  &::-webkit-scrollbar {
    /* webkit */
    display: none;
  }

  ${({ $fixedWidths }) => $fixedWidths && css`
      ${TabItemContainer}, ${TabItem} {
        flex: 1;
        flex-shrink: 0;
      }
    `}
`;
const ActiveIndicator = styled.div`
  box-sizing: border-box;
  position: absolute;
  bottom: ${({ $indicatorOutside }) => $indicatorOutside ? "-2px" : 0};
  height: 2px;
  z-index: 2;

  background-color: ${cssVars.activeIndicator};

  left: 0;
  right: 100%;
  /* stylelint-disable-next-line property-disallowed-list */
  will-change: left, right;

  ${({ $active, $direction }) => {
  if (!$active)
    return css`
        transition:
          left 0ms cubic-bezier(0.35, 0, 0.25, 1),
          right 0ms cubic-bezier(0.35, 0, 0.25, 1);
      `;
  if ($direction === "right") {
    return css`
        transition:
          left 0.25s cubic-bezier(0.35, 0, 0.25, 1),
          right 0.12s cubic-bezier(0.35, 0, 0.25, 1);
      `;
  }
  return css`
      transition:
        left 0.12s cubic-bezier(0.35, 0, 0.25, 1),
        right 0.25s cubic-bezier(0.35, 0, 0.25, 1);
    `;
}}
`;
const Tabs = ({
  activeTabId,
  activityIndicatorOutside = false,
  className,
  disabled,
  fixedWidths,
  items,
  onChange
}) => {
  const activeEl = useRef(null);
  const prevActiveTabId = useRef(activeTabId);
  const animationResetFallbackTimeoutId = useRef(null);
  const [direction, setDirection] = useState("right");
  const [forceActiveIndicatorRedraw, setForceActiveIndicatorRedraw] = useState(0);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [muteFocusIndicator, setMuteFocusIndicator] = useState(true);
  const [activeIndicatorState, setActiveIndicatorState] = useState({
    left: 0,
    right: "100%"
  });
  useEffect(() => {
    const prevId = prevActiveTabId.current;
    prevActiveTabId.current = activeTabId;
    if (prevId === activeTabId) {
      return;
    }
    const prevTabIndex = items.findIndex((tab) => tab.id === prevId);
    const activeTabIndex = items.findIndex((tab) => tab.id === activeTabId);
    setDirection(prevTabIndex <= activeTabIndex ? "right" : "left");
  }, [prevActiveTabId, activeTabId, items]);
  useLayoutEffect(() => {
    if (activeEl.current) {
      const { offsetLeft, offsetParent: parent, offsetWidth } = activeEl.current;
      if (!parent || !(parent instanceof HTMLElement))
        return;
      parent.scrollTo({
        left: offsetLeft - parent.clientWidth / 2 + offsetWidth / 2,
        behavior: isFirstRender ? "auto" : "smooth"
      });
    }
  }, [activeTabId, isFirstRender]);
  useLayoutEffect(() => {
    if (activeEl.current) {
      const { offsetLeft: left, offsetParent: parent, offsetWidth: width } = activeEl.current;
      if (!parent || !(parent instanceof HTMLElement))
        return;
      const right = parent.offsetWidth - left - width;
      setMuteFocusIndicator(true);
      setActiveIndicatorState({
        left,
        right
      });
      if (animationResetFallbackTimeoutId.current !== null) {
        clearTimeout(animationResetFallbackTimeoutId.current);
      }
      const timeoutId = setTimeout(() => {
        setMuteFocusIndicator(false);
      }, 300);
      animationResetFallbackTimeoutId.current = timeoutId;
      return () => {
        clearTimeout(timeoutId);
      };
    }
    return;
  }, [direction, activeTabId, fixedWidths, items, forceActiveIndicatorRedraw]);
  useLayoutEffect(() => {
    let unmounted = false;
    if (isFirstRender) {
      window.requestAnimationFrame(() => {
        if (!unmounted) {
          setIsFirstRender(false);
        }
      });
    }
    return () => {
      unmounted = true;
    };
  }, []);
  useEffect(() => {
    const resizeListener = () => {
      setForceActiveIndicatorRedraw(Date.now());
    };
    const opts = { passive: true };
    window.addEventListener("resize", resizeListener, opts);
    return () => {
      window.removeEventListener("resize", resizeListener, opts);
    };
  });
  return /* @__PURE__ */ jsxs(Root, { $disabled: disabled, $fixedWidths: fixedWidths, className, children: [
    items.map((tab) => {
      const active = tab.id === activeTabId;
      return /* @__PURE__ */ jsx(TabItemContainer, { ref: active ? activeEl : null, children: /* @__PURE__ */ jsx(
        TabItem,
        {
          $active: active,
          $activityIndicatorOutside: activityIndicatorOutside,
          $muteFocusIndicator: muteFocusIndicator,
          as: tab.component,
          ...tab.component ? {} : { type: "button" },
          disabled: Boolean(disabled || tab.disabled),
          onClick: (event) => {
            event.currentTarget.focus();
            if (onChange) {
              onChange(tab.id);
            }
          },
          children: /* @__PURE__ */ jsxs(TabItemContent, { children: [
            !!tab.leftContent && /* @__PURE__ */ jsx(LeftContent, { children: tab.leftContent }),
            /* @__PURE__ */ jsxs(LabelContainer, { children: [
              /* @__PURE__ */ jsx(Label, { $show: !active, children: tab.label }),
              /* @__PURE__ */ jsx(LabelOverlay, { $show: active, children: tab.label })
            ] }),
            !!tab.rightContent && /* @__PURE__ */ jsx(RightContent, { children: tab.rightContent })
          ] })
        }
      ) }, tab.id);
    }),
    /* @__PURE__ */ jsx(
      ActiveIndicator,
      {
        $active: !isFirstRender,
        $direction: direction,
        $indicatorOutside: activityIndicatorOutside,
        onTransitionEnd: () => {
          if (animationResetFallbackTimeoutId.current !== null) {
            clearTimeout(animationResetFallbackTimeoutId.current);
          }
          setMuteFocusIndicator(false);
        },
        style: activeIndicatorState
      }
    )
  ] });
};
export {
  Tabs
};
//# sourceMappingURL=index.js.map
